import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { useEffect, useRef, useState } from "react";
import { DndProvider } from "react-dnd-multi-backend";
import BinaryBoxDivision from "../components/BinaryBoxDivision.jsx";
import Coin from "../components/Coin";
import { DraggableMessage } from "../components/Draggables";
import InputBox from "../components/InputBox";
import {
  BINARY_FONT,
  BINARY_SYSTEM,
  COLOR_DIVISOR,
  COLOR_HIGHLIGHTED,
  FONT
} from "../defaults";
import { convertToAlternate, increaseProgress } from "../helpers";
import coin_1 from "../images/coin_1.png";
import coin_16 from "../images/coin_16.png";
import coin_2 from "../images/coin_2.png";
import coin_32 from "../images/coin_32.png";
import coin_4 from "../images/coin_4.png";
import coin_8 from "../images/coin_8.png";
import {
  buttonCheck,
  level2BinaryContinueDistributing,
  level2BinaryNoLeadingZeros,
  level2RomanDropLargerFirst,
  levelDivisionTask1,
  levelDivisionTask2,
  levelExchange,
  levelExchangeTooSmall,
  levelExchangeCoins,
  levelNotEnoughCoins,
  levelExchangeTooMany,
  levelDivisionTaskBoxes,
  levelWrongBox,
  levelWrongDistributed,
  levelWrongNotDistributed,
  levelCorrect,
  levelNoInputMulti
} from "../strings";


const LevelNumber = 3;

function calculateDividend(currentProgress) {
  const numbers = [17, 23, 41];
  if (currentProgress >= numbers.length) {
    return Math.round(Math.random() * 44) + 5; // Range 5-49
  }
  return numbers[currentProgress];
}

function calculateDivisor(currentProgress) {
  const numbers = [2, 3, 3];
  if (currentProgress >= numbers.length) {
    return Math.round(Math.random() * 1) + 2; // Range 2-3
  }
  return numbers[currentProgress];
}

const coinImages = {
  0: coin_32,
  1: coin_16,
  2: coin_8,
  3: coin_4,
  4: coin_2,
  5: coin_1
};

function Level3Binary({levelProgress, setLevelProgress, showMessage, setShowMessage, disableInput, setDisableInput}) {
  const [dividend, setDividend] = useState(
    calculateDividend(levelProgress[LevelNumber])
  );
  const [divisor, setDivisor] = useState(
    calculateDivisor(levelProgress[LevelNumber])
  );
  const [coins, setCoins] = useState([]); // How many coins to render on the left side.
  const [result, setResult] = useState([]); // How many coins should be in each divisor box at the end of the game.
  const [coinsInBoxes, setCoinsInBoxes] = useState( // How many coins have been distributed so far in each divisor box.
    Array.from({ length: 5 }, () => [0, 0, 0, 0, 0, 0])
  );
  const [exchanged, setExchanged] = useState([false,false,false,false,false,false]); // Whether a certain coin type has been exchanged (for another). If this is true, then no coins of that type are left over.
  const [distributionState, setDistributionState] = useState(0); // State variable with divisor amount of states (2 or 3). Corresponds to boxIndex. (0 - coins equally distributed (default), 1 - first box has just gotten a coin. If divisor===3: 2 - second box has just gotten a coin (if divisor===2 this is equivalent to state 0, hence not used)) 
  const [message, setMessage] = useState("");
  const [correct, setCorrect] = useState(false);
  


  useEffect(() => {
    let divd = calculateDividend(levelProgress[LevelNumber]);
    let divr = calculateDivisor(levelProgress[LevelNumber]);
    setDividend(divd);
    setDivisor(divr);
  }, [levelProgress]);

  useEffect(() => { // Initialization, executed each time a new exercise (new dividend & divisor) is loaded.
    let dividendString = convertToAlternate(BINARY_SYSTEM, dividend).padStart(6, 0); // Pad beginning with 0s up to length of 6.
    let dividendArray = Array.from(dividendString).map(Number); // map(Number) converts array of strings to array of numbers.
    let resultString = convertToAlternate(BINARY_SYSTEM, Math.floor(dividend / divisor)).padStart(6, 0); // Pad beginning with 0s up to length of 6.
    let resultArray = Array.from(resultString).map(Number);
    setExchanged([false,false,false,false,false,false]);
    setCoins(dividendArray);
    setResult(resultArray); // E.g. if result is 5, this is [0,0,0,1,0,1]
    setCoinsInBoxes(Array.from({ length: 5 }, () => [0, 0, 0, 0, 0, 0]));
    setDistributionState(0); // Reset state.
  }, [dividend, divisor]);

  function handleDropCoin(item, boxIndex) { // item[0] is index that corresponds to coin type.
    const index = item[0];
    const allBoxesFilled = coinsInBoxes
      .slice(0, divisor)
      .every((box) => box[index] >= result[index]);
    
    for (let i = 0; i < index; ++i) { // Loop to check all indices smaller than index. Checks if any larger coin types still exist. If they do, drop is not permitted.
      let someCoinsLeft = !exchanged[i] && coins[i] > 0; // Check if for this larger coin type i some coins are left. Some coins are left iff the coins were not exchanged and not all were dropped.
      if (someCoinsLeft) { // If for any of the larger coin types some coins still exist, then user is not allowed to drop the coin they just tried to drop.
        setMessage(level2RomanDropLargerFirst);
        displayMessage();
        return; // Exit function
      }
    }
    if (allBoxesFilled && index === 5) {
      setMessage(levelNotEnoughCoins);
      displayMessage();
    } else if (allBoxesFilled) {
      setMessage(levelExchangeCoins);
      displayMessage();
    } else if (coinsInBoxes[boxIndex][index] >= result[index]) {
      setMessage(levelWrongBox);
      displayMessage();
    } else {
      let newCoins = [...coins];
      newCoins[index] -= 1;
      setCoins(newCoins);
      const newCoinsInBoxes = [...coinsInBoxes];
      newCoinsInBoxes[boxIndex][index] += 1;
      setCoinsInBoxes(newCoinsInBoxes);
      setDistributionState(n => ((n+1) % divisor)); // Increment distribution state
    }
  }

  // Hint: Smaller index means larger coin type.
  function handleExchange(index) {
    if (index === 5) { // Case: Exchange unsuccessful, smallest coin cannot be exchanged.
      setMessage(levelExchangeTooSmall);
      displayMessage();
      return;
    } 
    if (distributionState > 0) { // Case: Exchange unsuccessful, not all boxes have equal amount of coins.
      setMessage(level2BinaryContinueDistributing);
      displayMessage();
      return;
    }
    for (let i = 0; i < index; ++i) { // Loop to check all indices smaller than index. Checks if any larger coin types still exist. If they do, exchange is not permitted.
        let someCoinsLeft = !exchanged[i] && coins[i] > 0; // Check if for this larger coin type i some coins are left. Some coins are left iff the coins were not exchanged and not all were dropped.
        if (someCoinsLeft) { // Case: Exchange unsuccessful, larger coins still exist. If for any of the larger coin types some coins still exist, then user is not allowed to exchange the coin type they just tried to exchange.
        setMessage(level2RomanDropLargerFirst);
        displayMessage();
        return; // Exit function
        }
    }
    if (index < 5 && coins[index] < divisor) { // Case: Exchange successful.
      const newCoins = [...coins];
      newCoins[index] = 0;
      newCoins[index + 1] += coins[index] * 2;
      const newExchanged = [...exchanged];
      newExchanged[index] = true;
      setExchanged(newExchanged);
      setCoins(newCoins);
    } else { // Case: Exchange unsuccessful, enough coins to distribute instead.
      setMessage(levelExchangeTooMany);
      displayMessage();
    }
  }

  const handleCorrect = () => {
    setCorrect(true);
    setMessage(levelCorrect)
    displayMessage();
  };

  const handleIncorrect = (leadingZero) => {
    let allBoxesFilled = coinsInBoxes
      .slice(0, divisor)
      .every((box) => box[0] >= result[0]);
    allBoxesFilled =
      allBoxesFilled &&
      coinsInBoxes.slice(0, divisor).every((box) => box[1] >= result[1]);
    allBoxesFilled =
      allBoxesFilled &&
      coinsInBoxes.slice(0, divisor).every((box) => box[2] >= result[2]);
    allBoxesFilled =
      allBoxesFilled &&
      coinsInBoxes.slice(0, divisor).every((box) => box[3] >= result[3]);
    allBoxesFilled =
      allBoxesFilled &&
      coinsInBoxes.slice(0, divisor).every((box) => box[4] >= result[4]);
    allBoxesFilled =
      allBoxesFilled &&
      coinsInBoxes.slice(0, divisor).every((box) => box[5] >= result[5]);
    if (allBoxesFilled) {
      setMessage(levelWrongDistributed);
    } else {
      setMessage(levelWrongNotDistributed);
    }
    if (leadingZero) setMessage(level2BinaryNoLeadingZeros); // This message overrides the normal messages.
    displayMessage();
  };

  const handleNoInput = () => {
    setMessage(levelNoInputMulti);
    displayMessage();
  };

  const displayMessage = () => {
    setDisableInput(true);
    setShowMessage(true);
  };

  const handleCloseMessage = () => {
    if(correct) { // Increase Progress only upon closing popup message.
      increaseProgress(levelProgress, setLevelProgress, LevelNumber, BINARY_SYSTEM);
    }
    setDisableInput(false);
    setCorrect(false);
  };

  
  const containerArrayWidth = 70; // Used to scale the sizes of containers and coins.

  const exchangeStyle = {
    padding: "1dvh",
    marginBottom: "2dvh",
    width: "10dvh",
    fontFamily: FONT,
    color: "white",
    fontSize: "1.6dvh",
    textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
  };

  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
    maxWidth: "12dvh", // Four times max coin width.
    maxHeight: "30dvh",
    alignItems: "center",
    justifyContent: "center",
  };

 
  const containerArrayStyle = {
    display: "flex", 
    flexDirection: "row", 
    marginRight: "38dvh", 
    justifyContent: "center",
    columnGap: "4dvh", 
    maxWidth: 1.5*containerArrayWidth+"dvh",
  };

  const textStyle = {
    fontFamily: BINARY_FONT,
    fontSize:"4dvh", 
    color:"white",
    filter: "drop-shadow(0.3svh 0.3svh 0.3svh rgba(0, 0, 0, 0.5))" // Using textShadow in combination with fontWeight : "bold" looks weird on iPads. Using filter circumvents this issue.
  };

  /** Renders coins on the left side. */
  function RenderContainerArray() {
    let containerArray = [];
    for (let id = 0; id < coins.length; ++id) { // Render all coins for each coin type.
      const showCoin = coins.slice(0, id + 1).some(coin => coin > 0);
      containerArray.push( // Individual container code starts here.
        <div style={containerStyle/*Cheat to have it centered. At marginBottom 3.5dvh it would be centered, but since exchangeStyle also has marginBottom, need to add it.*/}> 
            <button onClick={() => handleExchange(id)} style={{...exchangeStyle, visibility:  coins[id] >= 1 ? "visible" : "hidden"}} className="exchange-button" disabled={disableInput}>
                {levelExchange}
            </button>
            {coins[id] >= 1 && <span style={{display: "flex", width: "9.5dvh", height: "9.5dvh", opacity: "1"}}><Coin key={[id, 0]} id={[id, 0]} coinImage={coinImages[id]}/></span>}
            {coins[id] === 0 && <span style={{display: "flex", width: "9.5dvh", height: "9.5dvh", opacity: "0.3"}}><img style={{width: "100%", height: "100%"}} src={coinImages[id]} alt="coin"/></span>}
            <span style={{...textStyle, opacity: showCoin ? "1" : "0.5", fontWeight: showCoin ? "bold" : "normal", color: showCoin ? COLOR_HIGHLIGHTED : "white"}}>{coins[id]}</span> {/* Partially LLM-generated line. If a coin with lower coinId than current has been dropped (i.e. there is a 1 or higher to the left somewhere), then strengthen all numbers to the right. */}
        </div>
      );
    }
    return containerArray;
  }
 
  
  const inputBoxRef = useRef();

  // LLM-generated function.
  const callCheckInput = () => {
    // Call the checkInput function in the child via the ref
    if (inputBoxRef.current) {
      inputBoxRef.current.checkInput();
    }
  };

  return (
    <div>
      <div
        className="Welcome-label"
        style={{ fontFamily: FONT, fontSize: "3.5dvh" }}
      >
        {levelDivisionTask1}
        <span style={{fontFamily: BINARY_FONT, color: COLOR_HIGHLIGHTED, fontSize: "5dvh"}}>{convertToAlternate(BINARY_SYSTEM, dividend)}</span>
        {levelDivisionTask2}
        <span style={{ color: COLOR_DIVISOR }}>{divisor}</span>?<br></br>
        {levelDivisionTaskBoxes}
      </div>
      
      <DndProvider options={HTML5toTouch}>
        {showMessage && <DraggableMessage show={showMessage} setShow={setShowMessage} handleClose={handleCloseMessage} message={message}/>} {/* Conditionally rendering it with showMessage makes the message re-render, and hence center itself, every time. */}
        <div style={{ display: "flex", flexDirection: "column"}}>
          <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: "30dvh"}}>
            <span style={containerArrayStyle}><RenderContainerArray/></span>
            <div style={{position: "absolute", top: "20%", left: "70%", /*Where the boxes are on screen.*/}}>
              {Array.from({ length: divisor }).map((_, idx) => (
                <BinaryBoxDivision
                  id={idx}
                  droppedCoins={coinsInBoxes[idx]}
                  onDropCoin={handleDropCoin}
                  coinSize={5.5}
                />
              ))}
            </div>
          </div>
          <div style={{ display: "flex", marginTop: "3dvh", marginLeft: "auto", marginRight: "auto", paddingRight: containerArrayStyle.marginRight/*Padding same as containerArray has marginRight, so they align again.*/, justifyContent: "center"}}>
            <InputBox
              handleCorrect={handleCorrect}
              handleIncorrect={handleIncorrect}
              handleNoInput = {handleNoInput}
              dividend={dividend}
              divisor={divisor}
              remainder={dividend % divisor}
              whichSystem={BINARY_SYSTEM}
              showMessage={showMessage}
              disableInput={disableInput}
              ref={inputBoxRef}
            ></InputBox>
          </div>
          <button
            onClick={callCheckInput}
            className="checkAnswer-button"
            disabled={disableInput}
          >
          {buttonCheck}
          </button>
        </div>
      </DndProvider>
    </div>
  );
}

export default Level3Binary;
